<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { TrashIcon, PlusIcon, InformationCircleIcon, PencilIcon } from '@heroicons/vue/24/outline'

import CreateJobRequestModal from './CreateJobRequestModal.vue';
import EditJobRequestModal from './EditJobRequestModal.vue';

import { useStore } from 'vuex';
import { dispatchNotification } from './Notification';

const props = defineProps(['jobRequests', 'userId']);

const jobRequests = ref(JSON.parse(props.jobRequests).length ? JSON.parse(props.jobRequests) : []);

const store = useStore();
const csrfToken = store.state.csrfToken;

const jobRequestToEdit = ref();
const showCreateJobRequestModal = ref(false);
const showEditJobRequestModal = ref(false);

const addJobRequest = (jobRequest) => {
    jobRequests.value.push(jobRequest);
    showCreateJobRequestModal.value = false;
}

const updateJobRequest = (jobRequest) => {
    const jobRequestIndex = jobRequests.value.findIndex(currentJobRequest => currentJobRequest.id === jobRequest.id);
    if (jobRequestIndex !== -1) {
        jobRequests.value[jobRequestIndex] = jobRequest;
    }
    showEditJobRequestModal.value = false;
}

const editJobRequest = (index) => {
    jobRequestToEdit.value = jobRequests.value[index];
    showEditJobRequestModal.value = true;
}

const deleteJobRequest = (index) => {
    if (!confirm("Möchten Sie die Interesse wirklich unwiderruflich löschen?")) return;
    const id = jobRequests.value[index].id;

    fetch(`/api/job-requests/${id}`, {
        method: 'DELETE',
        headers: {
            Accept: 'application/json',
            'X-CSRF-TOKEN': csrfToken
        }
    }).then(() => {
        dispatchNotification({ title: 'Aktion erfolgreich', content: 'Die Interesse wurde erfolgreich gelöscht.', type: 'success', duration: 5000 });
        jobRequests.value.splice(index, 1);
    }).catch((error) => console.log(error));
}

const isValidJSON = (str) => {
    try {
        JSON.parse(str);
        return true;
    } catch (e) {
        return false;
    }
}
</script>

<template>
    <div class="">
        <div
            class="tw-gap-x-6 tw-gap-y-4 sm:tw-grid-cols-6 tw-rounded tw-p-5 tw-relative tw-group tw-bg-white tw-border-dashed tw-border">

            <h3 class="sm:tw-col-span-6">Wonach suchst du?</h3>

            <div v-for="(jobRequest, index) in jobRequests" :key="index"
                class="tw-flex tw-flex-row tw-justify-between tw-items-center tw-mt-8">
                <div class="tw-rounded-md sm:tw-flex sm:tw-items-start sm:tw-justify-between">
                    <div class="sm:tw-flex sm:tw-items-start">

                        <span class="tw-inline-flex tw-rounded-lg tw-p-3 tw-bg-teal-50 tw-text-teal-700">
                            <svg class="tw-h-6 tw-w-6" xmlns="http://www.w3.org/2000/svg" fill="none"
                                viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round"
                                    d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
                            </svg>
                        </span>

                        <div class="tw-mt-3 sm:tw-ml-4 sm:tw-mt-0">
                            <div class="tw-text-sm tw-font-medium tw-text-gray-900">
                                {{ jobRequest.title }} <span class="tw-text-gray-600 tw-font-normal">&middot;
                                    {{ jobRequest.type }}</span>
                            </div>
                            <div class="tw-mt-1 tw-text-sm tw-text-gray-600 sm:tw-flex sm:tw-items-center">
                                <div>{{ isValidJSON(jobRequest.industry) ? JSON.parse(jobRequest.industry).join(', ') :
                jobRequest.industry }}</div>
                            </div>
                        </div>

                    </div>
                </div>
                <div class="tw-space-x-2 tw-min-w-[100px]">
                    <span @click="editJobRequest(index)"
                        class="tw-inline-flex tw-rounded-sm tw-p-2 tw-bg-yellow-600/10 hover:tw-bg-yellow-600/20 hover:tw-text-yellow-900 tw-text-yellow-800">
                        <PencilIcon class="tw-h-4" />
                    </span>
                    <span @click="deleteJobRequest(index)"
                        class="tw-inline-flex tw-rounded-sm tw-p-2 tw-bg-red-600/10 hover:tw-bg-red-600/20 hover:tw-text-red-900 tw-text-red-800">
                        <TrashIcon class="tw-h-4" />
                    </span>
                </div>
            </div>

            <div @click="showCreateJobRequestModal = true"
                class="sm:tw-col-span-6 tw-bg-gray-400/10 hover:tw-bg-gray-400/20 tw-p-2 tw-rounded tw-text-sm tw-flex tw-items-center tw-justify-center tw-gap-1 tw-cursor-pointer tw-mt-4">
                Weitere Berufe oder Branchen eingeben
                <PlusIcon class="tw-w-3" />
            </div>

        </div>
    </div>

    <CreateJobRequestModal :user-id="props.userId" v-if="showCreateJobRequestModal"
        @closes="showCreateJobRequestModal = false" @created="addJobRequest" />

    <EditJobRequestModal :user-id="props.userId" v-if="showEditJobRequestModal" :job-request="jobRequestToEdit"
        @closes="showEditJobRequestModal = false" @updated="updateJobRequest" />
</template>
